export interface NumberFormatterOptions {
  decimalPlaces?: number;
  useAbbreviation?: boolean;
  minimumValue?: number; // Minimum value to start abbreviating
}

const abbreviations = [
  { value: 1e12, suffix: 'T' },
  { value: 1e9, suffix: 'B' },
  { value: 1e6, suffix: 'M' },
  { value: 1e3, suffix: 'K' },
];

export function formatLargeNumber(
  value: number,
  options: NumberFormatterOptions = {}
): string {
  const {
    decimalPlaces = 1,
    useAbbreviation = true,
    minimumValue = 1000,
  } = options;

  // Return formatted number without abbreviation if disabled or below minimum
  if (!useAbbreviation || Math.abs(value) < minimumValue) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPlaces,
    });
  }

  // Find appropriate abbreviation
  for (const { value: threshold, suffix } of abbreviations) {
    if (Math.abs(value) >= threshold) {
      const abbreviated = value / threshold;
      return `${abbreviated.toFixed(decimalPlaces)}${suffix}`;
    }
  }

  // If no abbreviation applies, return with standard formatting
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces,
  });
}

export function formatNumber(
  value: number,
  options: NumberFormatterOptions = {}
): string {
  return formatLargeNumber(value, options);
}

export function formatCompactNumber(value: number): string {
  return formatLargeNumber(value, {
    decimalPlaces: 1,
    useAbbreviation: true,
    minimumValue: 1000,
  });
}

export function formatDetailedNumber(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Rounds a number to 3 decimal places for internal calculations
 * This prevents floating point precision issues and long decimal values
 */
export function roundToThousandths(value: number): number {
  return Math.round(value * 1000) / 1000;
}